'use client';

import { useState, useRef } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { IconDefinition } from '@fortawesome/pro-light-svg-icons';

import { Icon } from '@/components/ui/icon.component';
import useIsMobile from '@/hooks/use-is-mobile.hook';
import { SearchType } from '@/types/most-search-type';

interface FilterPillProps {
  id: string;
  label: string;
  icon: IconDefinition & IconProp;
  action: () => void;
  variant: 'success' | 'light';
  className?: string;
  dataTestId?: string;
}

const getVariantClasses = (variant: 'success' | 'light') => {
  switch (variant) {
    case 'success':
      return 'bg-success-100 text-success-700 border-success-400';
    case 'light':
      return 'bg-gray-100 text-gray-700 border-gray-400';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-400';
  }
};

export const FilterPill = ({
  id,
  label,
  icon,
  action,
  variant,
  className,
  dataTestId,
}: FilterPillProps) => {
  const isMobile = useIsMobile();
  const [isDragging, setIsDragging] = useState(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleClick = () => {
    if (!isDragging) {
      action();
      if (isMobile) {
        const firstElement = document.getElementById(SearchType.ONLINE_CONSULTATION);
        const currentElement = document.getElementById(id);
        if (firstElement && currentElement) {
          currentElement.scrollLeft = firstElement?.offsetLeft || 0;
        }
      }
    }
    setIsDragging(false);
    startPosRef.current = null;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    startPosRef.current = { x: e.clientX, y: e.clientY };
    setIsDragging(false);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (startPosRef.current) {
      const deltaX = Math.abs(e.clientX - startPosRef.current.x);
      const deltaY = Math.abs(e.clientY - startPosRef.current.y);

      if (deltaX > 10 || deltaY > 10) {
        setIsDragging(true);
      }
    }
  };

  return (
    <div
      id={id}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handleClick}
      className={
        `flex-shrink-0 px-2 py-1 rounded-md border w-fit flex items-center gap-1 text-base font-semibold cursor-pointer
         ${getVariantClasses(variant)}
         ${className || ''}`
      }
      data-testid={dataTestId}
    >
      <Icon data-testid="pill-start-icon" icon={icon} size="sm" className="mt-[1px]" />
      <div className="flex justify-content truncate">
        <span data-testid="pill-label" className="truncate">
          {label}
        </span>
      </div>
    </div>
  );
};
